import { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { ChevronLeft, Check, X, User } from "lucide-react";
import PolicyDetailsHeader from "../../components/payments/PolicyDetailsHeader";
import PolicyCoverVisual from "../../components/payments/PolicyCoverVisual";
import MobileMoneyForm from "../../components/payments/MobileMoneyForm";
import CreditCardForm from "../../components/payments/CreditCardForm";
import IntercityTravelForm from "../../components/payments/IntercityTravelForm";
import OkadaForm from "../../components/payments/OkadaForm";
import TaxiForm from "../../components/payments/TaxiForm";
import ReviewStep from "../../components/payments/ReviewStep";
import PaymentLoadingModal from "../../components/payments/PaymentLoadingModal";
import PaymentSuccessModal from "../../components/payments/PaymentSuccessModal";
import { formatMoney } from "../../utils/formatMoney";
import PaymentErrorModal from "../../components/payments/PaymentErrorModal";
import DocumentRequiredModal from "../../components/common/DocumentRequiredModal";
import { useAppSelector } from "../../store/hooks";
import policyService from "../../api/PolicyService";

const METHODS = [
  { key: "momo", label: "Mobile Money" },
  // { key: "card", label: "Credit/Debit Card" },
];

function withPurchaseStamp(coverageDuration) {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  const stamp = {
    purchase_date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    purchase_time: `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
    purchased_at: now.toISOString(),
  };

  if (coverageDuration && typeof coverageDuration === "object") {
    return { ...coverageDuration, ...stamp };
  }

  return stamp;
}

function BackCancelBar({ onBack, onCancel, className = "" }) {
  return (
    <div
      className={`h-11 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex h-full min-h-11 flex-1 items-center justify-center gap-1.5 px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-emerald-50 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500"
      >
        <ChevronLeft size={16} aria-hidden="true" />
        Back
      </button>
      <span className="w-px self-stretch bg-gray-200" aria-hidden="true" />
      <button
        type="button"
        onClick={onCancel}
        className="inline-flex h-full min-h-11 flex-1 items-center justify-center gap-1.5 px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-400"
      >
        <X size={16} aria-hidden="true" />
        Cancel
      </button>
    </div>
  );
}

export default function Checkout() {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const user = useAppSelector((s) => s.auth.user);
  const { state } = useLocation();
  const navigate = useNavigate();
  const [method, setMethod] = useState("momo");
  const [submitting, setSubmitting] = useState(false);

  // Additional details for different policy types
  const [additionalDetails, setAdditionalDetails] = useState({});

  // Buying for someone else (state kept for payload logic even though UI is commented out)
  // eslint-disable-next-line no-unused-vars
  const [isBuyingForSomeone, setIsBuyingForSomeone] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [beneficiaryInfo, setBeneficiaryInfo] = useState({
    fullName: "",
    relationship: "",
    phone: "",
    idType: "National ID",
    idNumber: "",
    idFront: null,
    idBack: null,
  });

  // Modal states
  const [showLoading, setShowLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // Step management
  const [currentStep, setCurrentStep] = useState(1);

  const isDocumentComplete = useMemo(() => {
    if (!user) return false;
    return Boolean(
      user.document_type &&
        user.card_number &&
        user.issue_date &&
        user.expiry_date &&
        user.id_card_front &&
        user.id_card_back
    );
  }, [user]);

  const sidebarSlotRef = useRef(null);
  const [sidebarBox, setSidebarBox] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = "";
  }, []);

  useEffect(() => {
    const slot = sidebarSlotRef.current;
    if (!slot) return undefined;

    const update = () => {
      if (window.innerWidth < 1024) {
        setSidebarBox(null);
        return;
      }
      const rect = slot.getBoundingClientRect();
      setSidebarBox({ left: rect.left, width: rect.width });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(slot);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const { policyType, providerName, coverLabel, amount: initialAmount, coverageDuration, duration, days, selectedPackage, insuranceType, originalPolicy, insurance_provider_slug: stateInsuranceProviderSlug, fromQuote, quoteRequestId, policySlug: quotePolicySlug, policyName: quotePolicyName, packageName: quotePackageName, coverageDurationIndex: quoteCoverageDurationIndex } = useMemo(() => {
    // Quote-based payment: use quote state as-is
    if (state?.fromQuote === true) {
      const policy = state.originalPolicy || {};
      const providerNameFromPolicy = policy?.insurance_provider?.name ?? policy?.insurance_provider_name ?? state.providerName ?? "";
      return {
        policyType: state.policyType ?? policy?.policy_type ?? "travel_insurance",
        providerName: providerNameFromPolicy,
        coverLabel: state.packageName || state.coverLabel || "Quote",
        amount: state.amount ?? 0,
        coverageDuration: state.coverageDuration ?? null,
        duration: state.duration ?? null,
        selectedPackage: state.selectedPackage ?? null,
        insuranceType: state.insuranceType ?? policy?.short_description ?? "travel_insurance",
        originalPolicy: state.originalPolicy ?? null,
        insurance_provider_slug: state.insurance_provider_slug ?? policy?.insurance_provider?.slug ?? policy?.insurance_provider,
        fromQuote: true,
        quoteRequestId: state.quoteRequestId,
        policySlug: state.policySlug,
        policyName: state.policyName,
        packageName: state.packageName,
        coverageDurationIndex: state.coverageDurationIndex,
      };
    }
    if (state?.policyType || state?.insuranceType) return { ...state, fromQuote: false, quoteRequestId: null, policySlug: undefined, policyName: undefined, packageName: undefined, coverageDurationIndex: undefined };
    const fromSession = sessionStorage.getItem("pendingCheckout");
    if (fromSession) {
      const parsed = JSON.parse(fromSession);
      sessionStorage.removeItem("pendingCheckout");
      return { ...parsed, fromQuote: false, quoteRequestId: null, policySlug: undefined, policyName: undefined, packageName: undefined };
    }
    return {
      policyType: "daily_commuter",
      providerName: "",
      coverLabel: "2 Day Cover",
      amount: 0,
      coverageDuration: null,
      duration: null,
      selectedPackage: null,
      insuranceType: null,
      originalPolicy: null,
      insurance_provider_slug: null,
      fromQuote: false,
      quoteRequestId: null,
      policySlug: undefined,
      policyName: undefined,
      packageName: undefined,
      coverageDurationIndex: undefined,
    };
  }, [state]);

  const isTravelInsuranceWithBothTypes =
    insuranceType === "travel_insurance" && policyType === "travel_insurance";
  const isShortCheckout =
    insuranceType === "life_insurance" ||
    insuranceType === "general_insurance" ||
    isTravelInsuranceWithBothTypes;

  // Extract insurance_provider_slug from state or originalPolicy
  const insuranceProviderSlug = useMemo(() => {
    // First check if it's directly in the extracted state
    if (stateInsuranceProviderSlug) {
      return stateInsuranceProviderSlug;
    }
    
    // Also check raw state (in case it wasn't extracted)
    if (state?.insurance_provider_slug) {
      return state.insurance_provider_slug;
    }
    
    // Check sessionStorage
    const fromSession = sessionStorage.getItem("pendingCheckout");
    if (fromSession) {
      try {
        const parsed = JSON.parse(fromSession);
        if (parsed.insurance_provider_slug) {
          return parsed.insurance_provider_slug;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Extract from originalPolicy as fallback
    if (!originalPolicy?.insurance_provider) {
      return null;
    }
    
    // If insurance_provider is an object, check for slug field
    if (typeof originalPolicy.insurance_provider === 'object') {
      const slug = originalPolicy.insurance_provider.slug || originalPolicy.insurance_provider.id || null;
      if (slug) return slug;
    }
    
    // If it's a string, it might be the slug itself
    if (typeof originalPolicy.insurance_provider === 'string') {
      return originalPolicy.insurance_provider;
    }
    
    return null;
  }, [originalPolicy, state, stateInsuranceProviderSlug]);

  const amount = useMemo(() => {
    if (additionalDetails.amount) {
      return additionalDetails.amount;
    }
    
    if (coverageDuration && typeof coverageDuration === 'object' && coverageDuration.price) {
      return coverageDuration.price;
    }
    
    // Use initial amount from state
    if (initialAmount && initialAmount > 0) {
      return initialAmount;
    }
    
    // Fallback to selectedPackage premium
    if (selectedPackage?.premium) {
      return selectedPackage.premium;
    }
    
    return 0;
  }, [additionalDetails.amount, coverageDuration, initialAmount, selectedPackage?.premium]);

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace state={{ from: "/checkout" }} />;
  }

  const handleSubmit = async (payload) => {
    if (!isDocumentComplete) {
      setShowDocumentModal(true);
      return;
    }
    
    // Use the slug from payload if available, otherwise fallback to component state
    const finalInsuranceProviderSlug = payload.insuranceProviderSlug || insuranceProviderSlug;
    
    // Validate that insurance_provider_slug is not null
    if (!finalInsuranceProviderSlug) {
      toast.error("Insurance provider information is missing. Please try again or contact support.");
      setSubmitting(false);
      return;
    }
    
    setPaymentData(payload);
    setShowLoading(true);
    setSubmitting(true);

    try {
      const apiPayload = {
        phone_number: `233${payload.phone.substring(1)}`,
        network: payload.provider,
        insurance_provider_slug: finalInsuranceProviderSlug,
      };
      if (fromQuote && quoteRequestId) {
        apiPayload.quote_request_id = quoteRequestId;
        if (quoteCoverageDurationIndex != null) apiPayload.coverage_duration_index = quoteCoverageDurationIndex;
      }

      // Helper function to convert file to base64
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      // Prepare beneficiary data if buying for someone else
      let beneficiaryData = null;
      if (isBuyingForSomeone === true && beneficiaryInfo.fullName) {
        beneficiaryData = {
          fullName: beneficiaryInfo.fullName,
          relationship: beneficiaryInfo.relationship,
          phone: beneficiaryInfo.phone,
          idType: beneficiaryInfo.idType,
          idNumber: beneficiaryInfo.idNumber,
        };
        // Convert ID images to base64 if available
        if (beneficiaryInfo.idFront) {
          beneficiaryData.idFront = await fileToBase64(beneficiaryInfo.idFront);
        }
        if (beneficiaryInfo.idBack) {
          beneficiaryData.idBack = await fileToBase64(beneficiaryInfo.idBack);
        }
      }

      // Include coverage duration information if available
      // For Life, General, and Travel Insurance (when both policy_type and short_description are travel_insurance), use selectedPackage
      const isTravelInsuranceWithBothTypes = insuranceType === "travel_insurance" && policyType === "travel_insurance";
      if (insuranceType === "life_insurance" || insuranceType === "general_insurance" || isTravelInsuranceWithBothTypes) {
        const stampedCoverageDuration = withPurchaseStamp(selectedPackage);
        if (selectedPackage) {
          apiPayload.coverage_duration = stampedCoverageDuration;
        }
        // Include purchased_policy_data for Life, General, and Travel Insurance
        // Pass the complete package data including nested fields
        apiPayload.purchased_policy_data = {
          product_name: selectedPackage?.product_name || coverLabel,
          premium: amount,
          premium_duration: selectedPackage?.premium_duration || "monthly",
          duration: selectedPackage?.duration || duration || "N/A",
          description: selectedPackage?.description || "",
          policy_type: policyType,
          insurance_type: insuranceType,
          // Include all package-specific fields for proper display in Policy.jsx
          insurance_coverage_list: selectedPackage?.insurance_coverage_list || [],
          exclusions: selectedPackage?.exclusions || [],
          eligibility_criteria: selectedPackage?.eligibility_criteria || [],
          claim_process_steps: selectedPackage?.claim_process_steps || [],
          terms: selectedPackage?.terms || "",
          beneficiary: beneficiaryData,
          insurance_provider_slug: insuranceProviderSlug,
          coverageDuration: stampedCoverageDuration,
        };
      } else {
        // For travel insurance without both types matching (route-based), use the coverage from form data if available
        const baseCoverageDuration = additionalDetails.coverageDuration || coverageDuration || null;
        const stampedCoverageDuration = withPurchaseStamp(baseCoverageDuration);
        apiPayload.coverage_duration = stampedCoverageDuration;
        
        // Extract amount from route if coverageDuration is a route object
        let routeAmount = amount;
        if (coverageDuration && typeof coverageDuration === 'object' && coverageDuration.price) {
          routeAmount = coverageDuration.price;
        } else if (additionalDetails.amount) {
          routeAmount = additionalDetails.amount;
        }
        
        // Include purchased_policy_data for travel insurance
        apiPayload.purchased_policy_data = {
          ...additionalDetails,
          amount: routeAmount, // Ensure amount is included
          beneficiary: beneficiaryData, // null if buying for self
          coverageDuration: stampedCoverageDuration,
        };
      }

      const policySlug = getPolicySlug(policyType);
      const response = await policyService.purchasePolicy(
        apiPayload,
        policySlug
      );

      if (response.status && response.data?.status_code === 200) {
        setTransactionId(
          response.data?.data?.transaction_id || response.data?.data?.id
        );
        setShowLoading(false);
        setCurrentStep(getTotalSteps());
        setShowSuccess(true);
      } else {
        setShowLoading(false);
        setErrorMessage(response.message || "Payment failed");
        setShowError(true);
      }
    } catch (error) {
      setShowLoading(false);
      setErrorMessage(
        error.response?.data?.message ||
          "An error occurred while processing your payment. Please try again."
      );
      setShowError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const getPolicySlug = (policyType) => {
    if (fromQuote && quotePolicySlug) return quotePolicySlug;
    if (state?.policySlug) return state.policySlug;
    const fromSession = sessionStorage.getItem("pendingCheckout");
    if (fromSession) {
      const parsed = JSON.parse(fromSession);
      return parsed.policySlug;
    }
    return "daily-commuter-default";
  };

  const getPolicyTypeDisplayName = (policyType, insuranceType) => {
    // Handle Life and General Insurance
    if (insuranceType === "life_insurance") {
      return "Life Insurance";
    }
    if (insuranceType === "general_insurance") {
      const subcategoryMap = {
        health_insurance: "Health Insurance",
        home_insurance: "Home Insurance",
        accident_insurance: "Accident Insurance",
        fire_insurance: "Fire Insurance",
        marine_insurance: "Marine Insurance",
        liability_insurance: "Liability Insurance",
      };
      return subcategoryMap[policyType] || "General Insurance";
    }
    
    // Handle Travel Insurance
    const typeMap = {
      daily_commuter: "Daily Commuter",
      intercity_travel: "Intercity Travel",
      okada_rider: "Okada Rider",
      taxi_and_bolt: "Taxi & Bolt Driver",
    };
    return typeMap[policyType] || "Daily Commuter";
  };

  const getTotalSteps = () => {
    // Life, General, and Travel Insurance (when both policy_type and short_description are travel_insurance) have simpler flow (no additional forms)
    if (isShortCheckout) {
      return 3; // Details -> Payment -> Confirmation
    }
    return 4;
  };

  const getStepName = (step) => {
    if (isShortCheckout) {
      switch (step) {
        case 1:
          return "Details";
        case 2:
          return "Payment";
        case 3:
          return "Confirmation";
        default:
          return "Details";
      }
    }
    switch (step) {
      case 1:
        return "Details";
      case 2:
        return "Additional Info";
      case 3:
        return "Review";
      case 4:
        return "Payment";
      default:
        return "Details";
    }
  };

  const handleAdditionalDetailsSubmit = (details) => {
    setAdditionalDetails(details);
    if (isShortCheckout) {
      setAdditionalDetails({});
      setCurrentStep(2);
    } else {
      setCurrentStep(3);
    }
  };

  const handleReviewConfirm = () => {
    setCurrentStep(4);
  };

  const handleEditDetails = () => {
    if (isShortCheckout) {
      setCurrentStep(1);
    } else {
      setCurrentStep(2);
    }
  };

  const handleRetryPayment = () => {
    setShowError(false);
    setErrorMessage("");
    if (paymentData) {
      handleSubmit(paymentData);
    }
  };

  const coverDisplay =
    coverageDuration && typeof coverageDuration === "object" && coverageDuration.route_destination
      ? coverageDuration.route_destination
      : coverLabel ||
        (selectedPackage?.product_name
          ? `${selectedPackage.product_name} - ${selectedPackage.duration}`
          : "Package");

  const premiumPeriod = selectedPackage?.premium_duration
    ? { yearly: "year", monthly: "month", weekly: "week", daily: "day" }[
        String(selectedPackage.premium_duration).toLowerCase()
      ] || selectedPackage.premium_duration
    : null;

  const policyTypeName = getPolicyTypeDisplayName(policyType, insuranceType);
  const totalSteps = getTotalSteps();
  const durationDisplay =
    selectedPackage?.duration ||
    (coverageDuration?.display_value && coverageDuration?.display_unit
      ? `${coverageDuration.display_value} ${coverageDuration.display_unit}`
      : coverageDuration?.days
        ? `${coverageDuration.days} day${coverageDuration.days === 1 ? "" : "s"}`
        : coverageDuration?.hours
          ? `${coverageDuration.hours} hour${coverageDuration.hours === 1 ? "" : "s"}`
          : duration && duration !== "N/A"
            ? duration
            : days
              ? `${days} day${Number(days) === 1 ? "" : "s"}`
              : null);
  const routeDisplay =
    coverageDuration?.route_destination || coverageDuration?.route || null;
  const coverDescription = selectedPackage?.description || originalPolicy?.description || "";
  const coverageItems = (
    selectedPackage?.insurance_coverage_list ||
    coverageDuration?.insurance_coverage_list ||
    originalPolicy?.insurance_coverage_list ||
    []
  )
    .filter((item) => item && (typeof item === "string" || item.title))
    .slice(0, 5);
  const holderName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  const holderContact = user?.email || user?.phone_number || "";
  const confirmFacts = [
    { label: "Policy type", value: policyTypeName },
    providerName ? { label: "Provider", value: providerName } : null,
    { label: "Cover", value: coverDisplay },
    durationDisplay ? { label: "Duration", value: durationDisplay } : null,
    routeDisplay ? { label: "Route", value: routeDisplay } : null,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      <AnimatePresence>
        {showDocumentModal && (
          <DocumentRequiredModal
            isOpen={showDocumentModal}
            onClose={() => setShowDocumentModal(false)}
            onGoToProfile={() => {
              setShowDocumentModal(false);
              navigate("/settings");
            }}
            title="Documents Required"
            message="Please upload your required documents in your profile before completing this purchase."
          />
        )}
      </AnimatePresence>
      <div className="pt-20" />
      <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-7">
            <BackCancelBar
              className="inline-flex lg:hidden"
              onBack={() => navigate(-1)}
              onCancel={() => navigate("/insurance/choose")}
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Checkout</h1>
              <p className="mt-1 text-sm text-gray-600">
                Review your cover and complete payment. Your policy is issued instantly after a successful transaction.
              </p>
            </div>

            <ol className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-white p-3 sm:p-4">
              {Array.from({ length: totalSteps }, (_, index) => {
                const stepNumber = index + 1;
                const stepName = getStepName(stepNumber);
                const isComplete = currentStep > stepNumber;
                const isCurrent = currentStep === stepNumber;

                return (
                  <li key={stepNumber} className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold ${
                          isComplete || isCurrent
                            ? "bg-emerald-600 text-white"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {isComplete ? <Check size={14} aria-hidden="true" /> : stepNumber}
                      </span>
                      <span
                        className={`text-sm font-semibold ${
                          isCurrent ? "text-gray-900" : isComplete ? "text-emerald-800" : "text-gray-500"
                        }`}
                        aria-current={isCurrent ? "step" : undefined}
                      >
                        {stepName}
                      </span>
                    </div>
                    {stepNumber < totalSteps && (
                      <span
                        className={`mx-1 hidden h-px w-8 sm:block ${
                          isComplete ? "bg-emerald-500" : "bg-gray-200"
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </li>
                );
              })}
            </ol>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
              <AnimatePresence mode="wait">
                {currentStep === 1 && (
                  <motion.div
                    key="details"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex h-full min-h-0 flex-1 flex-col"
                  >
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        {fromQuote ? "Quote payment" : "Confirm your cover"}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        {fromQuote
                          ? "Complete payment for your quoted amount."
                          : "Review these details, then continue to payment."}
                      </p>
                      {fromQuote && (
                        <p className="mt-4 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-800">
                          Paying quoted amount for: {quotePolicyName || quotePackageName || coverLabel}
                        </p>
                      )}
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {confirmFacts.map((fact) => (
                        <div
                          key={fact.label}
                          className="rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3"
                        >
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            {fact.label}
                          </p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">{fact.value}</p>
                        </div>
                      ))}
                    </div>

                    {coverDescription ? (
                      <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-gray-600">
                        {coverDescription}
                      </p>
                    ) : null}

                    <div className="mt-5">
                      <h3 className="text-sm font-semibold text-gray-900">What&apos;s included</h3>
                      <ul className="mt-3 space-y-2">
                        {(coverageItems.length > 0
                          ? coverageItems.map((item) => ({
                              title: typeof item === "string" ? item : item.title,
                              limit: typeof item === "object" ? item.limit : null,
                            }))
                          : [
                              { title: "Digital policy issued immediately after payment", limit: null },
                              { title: "Cover starts once Mobile Money is confirmed", limit: null },
                              { title: "Claims support through TripSecure", limit: null },
                            ]
                        ).map((item) => (
                          <li key={item.title} className="flex items-start gap-2 text-sm text-gray-700">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                            <span>
                              {item.title}
                              {item.limit ? (
                                <span className="ml-1 font-medium text-emerald-700">
                                  · ₵{item.limit}
                                </span>
                              ) : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* ============================================
                        BUYING FOR SOMEONE ELSE FEATURE - COMMENTED OUT
                        Uncomment the code below to reactivate this feature
                        ============================================ */}
                    {/* 
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <p className="mb-3 text-sm font-semibold text-gray-900">
                        Are you buying this policy for someone else?
                      </p>
                      <div className="flex gap-3">
                        {[
                          { key: false, label: "No, for myself" },
                          { key: true, label: "Yes, for someone else" },
                        ].map((opt) => (
                          <button
                            key={String(opt.key)}
                            onClick={() => setIsBuyingForSomeone(opt.key)}
                            className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-colors ${
                              isBuyingForSomeone === opt.key
                                ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                : "border-gray-300 bg-white text-gray-700 hover:border-emerald-300"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {isBuyingForSomeone === true && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-4 space-y-4 bg-blue-50 rounded-xl border border-blue-200"
                      >
                        <h4 className="text-sm font-semibold text-gray-900">
                          Beneficiary Information
                        </h4>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block mb-1.5 text-xs font-medium text-gray-700">
                              Full Name *
                            </label>
                            <input
                              type="text"
                              value={beneficiaryInfo.fullName}
                              onChange={(e) =>
                                setBeneficiaryInfo({
                                  ...beneficiaryInfo,
                                  fullName: e.target.value,
                                })
                              }
                              className="px-3 w-full h-10 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                              required
                            />
                          </div>
                          <div>
                            <label className="block mb-1.5 text-xs font-medium text-gray-700">
                              Relationship *
                            </label>
                            <input
                              type="text"
                              value={beneficiaryInfo.relationship}
                              onChange={(e) =>
                                setBeneficiaryInfo({
                                  ...beneficiaryInfo,
                                  relationship: e.target.value,
                                })
                              }
                              placeholder="e.g., Spouse, Child, Parent"
                              className="px-3 w-full h-10 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                              required
                            />
                          </div>
                          <div>
                            <label className="block mb-1.5 text-xs font-medium text-gray-700">
                              Contact Number *
                            </label>
                            <input
                              type="tel"
                              value={beneficiaryInfo.phone}
                              onChange={(e) =>
                                setBeneficiaryInfo({
                                  ...beneficiaryInfo,
                                  phone: e.target.value,
                                })
                              }
                              className="px-3 w-full h-10 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                              required
                            />
                          </div>
                          <div>
                            <label className="block mb-1.5 text-xs font-medium text-gray-700">
                              ID Type *
                            </label>
                            <select
                              value={beneficiaryInfo.idType}
                              onChange={(e) =>
                                setBeneficiaryInfo({
                                  ...beneficiaryInfo,
                                  idType: e.target.value,
                                })
                              }
                              className="px-3 w-full h-10 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                            >
                              <option>National ID</option>
                              <option>Passport</option>
                              <option>Driver's License</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block mb-1.5 text-xs font-medium text-gray-700">
                              ID Number *
                            </label>
                            <input
                              type="text"
                              value={beneficiaryInfo.idNumber}
                              onChange={(e) =>
                                setBeneficiaryInfo({
                                  ...beneficiaryInfo,
                                  idNumber: e.target.value,
                                })
                              }
                              className="px-3 w-full h-10 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                              required
                            />
                          </div>
                          <div>
                            <label className="block mb-1.5 text-xs font-medium text-gray-700">
                              Upload ID Front
                            </label>
                            <div className="overflow-hidden relative rounded-xl border-2 border-gray-300 border-dashed transition-colors cursor-pointer hover:border-emerald-400 group">
                              {beneficiaryInfo.idFront ? (
                                <div className="relative">
                                  <img
                                    src={URL.createObjectURL(beneficiaryInfo.idFront)}
                                    alt="ID Front Preview"
                                    className="object-cover w-full h-48"
                                  />
                                  <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-40 opacity-0 transition-opacity group-hover:opacity-100">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setBeneficiaryInfo({
                                          ...beneficiaryInfo,
                                          idFront: null,
                                        });
                                      }}
                                      className="flex justify-center items-center w-10 h-10 text-white bg-red-500 rounded-full transition-colors hover:bg-red-600"
                                    >
                                      <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M6 18L18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                  <div className="absolute right-0 bottom-0 left-0 p-3 bg-gradient-to-t from-black to-transparent">
                                    <p className="text-sm font-medium text-white truncate">
                                      {beneficiaryInfo.idFront.name}
                                    </p>
                                    <p className="text-xs text-gray-200">
                                      {(beneficiaryInfo.idFront.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-8 text-center">
                                  <div className="mx-auto mb-3 w-12 h-12 text-gray-400">
                                    <svg
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </div>
                                  <p className="text-sm text-gray-600 group-hover:text-emerald-600">
                                    Click to upload ID front
                                  </p>
                                  <p className="mt-1 text-xs text-gray-500">
                                    PNG, JPG up to 2MB
                                  </p>
                                </div>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  setBeneficiaryInfo({
                                    ...beneficiaryInfo,
                                    idFront: e.target.files?.[0] || null,
                                  })
                                }
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block mb-1.5 text-xs font-medium text-gray-700">
                              Upload ID Back
                            </label>
                            <div className="overflow-hidden relative rounded-xl border-2 border-gray-300 border-dashed transition-colors cursor-pointer hover:border-emerald-400 group">
                              {beneficiaryInfo.idBack ? (
                                <div className="relative">
                                  <img
                                    src={URL.createObjectURL(beneficiaryInfo.idBack)}
                                    alt="ID Back Preview"
                                    className="object-cover w-full h-48"
                                  />
                                  <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-40 opacity-0 transition-opacity group-hover:opacity-100">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setBeneficiaryInfo({
                                          ...beneficiaryInfo,
                                          idBack: null,
                                        });
                                      }}
                                      className="flex justify-center items-center w-10 h-10 text-white bg-red-500 rounded-full transition-colors hover:bg-red-600"
                                    >
                                      <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M6 18L18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                  <div className="absolute right-0 bottom-0 left-0 p-3 bg-gradient-to-t from-black to-transparent">
                                    <p className="text-sm font-medium text-white truncate">
                                      {beneficiaryInfo.idBack.name}
                                    </p>
                                    <p className="text-xs text-gray-200">
                                      {(beneficiaryInfo.idBack.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-8 text-center">
                                  <div className="mx-auto mb-3 w-12 h-12 text-gray-400">
                                    <svg
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </div>
                                  <p className="text-sm text-gray-600 group-hover:text-emerald-600">
                                    Click to upload ID back
                                  </p>
                                  <p className="mt-1 text-xs text-gray-500">
                                    PNG, JPG up to 2MB
                                  </p>
                                </div>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  setBeneficiaryInfo({
                                    ...beneficiaryInfo,
                                    idBack: e.target.files?.[0] || null,
                                  })
                                }
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    */}

                    <div className="mt-auto flex justify-end border-t border-gray-100 pt-5">
                      <button
                        type="button"
                        onClick={() => {
                          // Validate if buying for someone else
                          if (isBuyingForSomeone === true) {
                            if (
                              !beneficiaryInfo.fullName ||
                              !beneficiaryInfo.phone ||
                              !beneficiaryInfo.idType ||
                              !beneficiaryInfo.idNumber
                            ) {
                              alert("Please fill in all required beneficiary information");
                              return;
                            }
                          }
                          if (isShortCheckout) {
                            setCurrentStep(2);
                          } else {
                            setCurrentStep(2);
                          }
                        }}
                        className="inline-flex h-11 cursor-pointer items-center justify-center rounded-xl bg-emerald-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      >
                        Continue
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Additional Information (for route/vehicle policies) */}
                {currentStep === 2 && !isShortCheckout && (
                  <motion.div
                    key="additional-info"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    {policyType === "intercity_travel" && (
                      <IntercityTravelForm
                        onSubmit={handleAdditionalDetailsSubmit}
                        onBack={() => setCurrentStep(1)}
                        initialData={additionalDetails}
                      />
                    )}
                    {(policyType === "okada_rider" || policyType === "daily_commuter") && (
                      <OkadaForm
                        onSubmit={handleAdditionalDetailsSubmit}
                        onBack={() => setCurrentStep(1)}
                        initialData={additionalDetails}
                        variant={policyType}
                      />
                    )}
                    {policyType === "taxi_and_bolt" && (
                      <TaxiForm
                        onSubmit={handleAdditionalDetailsSubmit}
                        onBack={() => setCurrentStep(1)}
                        initialData={additionalDetails}
                      />
                    )}
                  </motion.div>
                )}

                {/* Step 3: Review (for route/vehicle policies) */}
                {currentStep === 3 && !isShortCheckout && (
                  <motion.div
                    key="review"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <ReviewStep
                      policyData={{
                        policyType: policyTypeName,
                        providerName,
                        coverLabel: coverDisplay,
                        amount,
                      }}
                      additionalDetails={additionalDetails}
                      paymentMethod={method}
                      onEdit={handleEditDetails}
                      onConfirm={handleReviewConfirm}
                    />
                  </motion.div>
                )}

                {/* Step 2: Payment (for life, general, and travel insurance with both types) */}
                {currentStep === 2 && isShortCheckout && (
                  <motion.div
                    key="payment-daily"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div>
                      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h2 className="text-lg font-bold text-gray-900">Payment</h2>
                          <p className="mt-1 text-sm text-gray-600">Pay securely with Mobile Money.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(1)}
                          className="inline-flex h-11 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                        >
                          Back
                        </button>
                      </div>
                      
                      <p className="mb-3 text-sm font-semibold text-gray-800">
                        Payment method
                      </p>
                      <div className="mb-5 flex gap-2">
                        {METHODS.map((m) => (
                          <button
                            type="button"
                            key={m.key}
                            aria-pressed={method === m.key}
                            onClick={() => setMethod(m.key)}
                            disabled={submitting}
                            className={`inline-flex h-11 cursor-pointer items-center rounded-xl border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                              method === m.key
                                ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                                : "border-gray-200 bg-white text-gray-800 hover:border-orange-300"
                            }`}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>

                    {method === "momo" ? (
                      <MobileMoneyForm onSubmit={handleSubmit} insuranceProviderSlug={insuranceProviderSlug} />
                    ) : (
                      <CreditCardForm onSubmit={handleSubmit} insuranceProviderSlug={insuranceProviderSlug} />
                    )}

                      {submitting && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="mt-4 text-sm text-gray-600"
                        >
                          Processing payment... Please wait.
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Payment (for route/vehicle policies after review) */}
                {currentStep === 4 && !isShortCheckout && (
                  <motion.div
                    key="payment"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-gray-900">Payment</h2>
                        <p className="mt-1 text-sm text-gray-600">Pay securely with Mobile Money.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(3)}
                        className="inline-flex h-11 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      >
                        Back
                      </button>
                    </div>
                    
                    <p className="mb-3 text-sm font-semibold text-gray-800">
                      Payment method
                    </p>
                    <div className="mb-5 flex gap-2">
                      {METHODS.map((m) => (
                        <button
                          type="button"
                          key={m.key}
                          aria-pressed={method === m.key}
                          onClick={() => setMethod(m.key)}
                          disabled={submitting}
                          className={`inline-flex h-11 cursor-pointer items-center rounded-xl border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                            method === m.key
                              ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                              : "border-gray-200 bg-white text-gray-800 hover:border-orange-300"
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>

                    {method === "momo" ? (
                      <MobileMoneyForm onSubmit={handleSubmit} insuranceProviderSlug={insuranceProviderSlug} />
                    ) : (
                      <CreditCardForm onSubmit={handleSubmit} insuranceProviderSlug={insuranceProviderSlug} />
                    )}

                    {submitting && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-4 text-sm text-gray-600"
                      >
                        Processing payment... Please wait.
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <aside ref={sidebarSlotRef} className="lg:col-span-5">
            <div
              className={`flex flex-col gap-4 ${
                sidebarBox ? "lg:fixed lg:top-20 lg:z-20" : ""
              }`}
              style={
                sidebarBox
                  ? { left: sidebarBox.left, width: sidebarBox.width }
                  : undefined
              }
            >
            <BackCancelBar
              className="hidden lg:inline-flex"
              onBack={() => navigate(-1)}
              onCancel={() => navigate("/insurance/choose")}
            />
            <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
              <h2 className="text-base font-bold text-gray-900">Order summary</h2>
              <div className="mt-4">
                <PolicyDetailsHeader
                  policyType={policyTypeName}
                  providerName={providerName}
                  coverLabel={coverDisplay}
                />
              </div>
              <PolicyCoverVisual
                policyType={policyType}
                insuranceType={insuranceType}
                destination={routeDisplay}
                duration={durationDisplay}
                geography={originalPolicy?.geography}
                coverLabel={coverDisplay}
                holderName={holderName}
              />
              <dl className="mt-4 space-y-3 text-sm">
                {providerName ? (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Provider</dt>
                    <dd className="text-right font-medium text-gray-900">{providerName}</dd>
                  </div>
                ) : null}
                {routeDisplay ? (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Route</dt>
                    <dd className="text-right font-medium text-gray-900">{routeDisplay}</dd>
                  </div>
                ) : (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Cover</dt>
                    <dd className="text-right font-medium text-gray-900">{coverDisplay}</dd>
                  </div>
                )}
                {durationDisplay ? (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Duration</dt>
                    <dd className="text-right font-medium text-gray-900">{durationDisplay}</dd>
                  </div>
                ) : null}
                {holderName ? (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Policy holder</dt>
                    <dd className="text-right font-medium text-gray-900">{holderName}</dd>
                  </div>
                ) : null}
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Payment method</dt>
                  <dd className="text-right font-medium text-gray-900">
                    {METHODS.find((item) => item.key === method)?.label || "Mobile Money"}
                  </dd>
                </div>
                <div className="flex items-end justify-between gap-4 border-t border-gray-100 pt-3">
                  <dt className="text-sm font-semibold text-gray-900">Amount due</dt>
                  <dd className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{formatMoney(amount || 0, "₵ ")}</p>
                    {premiumPeriod && (
                      <p className="text-xs capitalize text-gray-500">per {premiumPeriod}</p>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Payment Modals */}
      <PaymentLoadingModal isOpen={showLoading} paymentData={paymentData} />

      <PaymentSuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        policyData={{
          policyType: policyTypeName,
          providerName,
          coverLabel: coverDisplay,
          amount,
        }}
        transactionId={transactionId}
      />

      <PaymentErrorModal
        isOpen={showError}
        onClose={() => setShowError(false)}
        onRetry={handleRetryPayment}
        errorMessage={errorMessage}
        paymentData={paymentData}
      />
    </div>
  );
}
